// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setCurrentShipTo from "@insite/client-framework/Store/Context/Handlers/SetCurrentShipTo";
import { getAddressFieldsDataView } from "@insite/client-framework/Store/Data/AddressFields/AddressFieldsSelector";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentShipTos from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTos";
import updateShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/UpdateShipTo";
import {
    getCurrentShipTosDataView,
    getCurrentShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import CustomerAddressForm from "@insite/content-library/Components/CustomerAddressForm";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { AddressInfoDisplayStyles } from "@store/Overrides/Components/AddressInfoDisplay";
import { addressProps } from "@store/Themes/common/props/address";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
/* Starter Custom Code - End */

/* Store Custom Code - Start */
import { useIsB2BCustomer } from "@store/Framework/Store/Data/BillTos/BillTosHooks";
/* Store Custom Code - End */

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    currentShipTo: getCurrentShipToState(state).value,
    currentShipTosDataView: getCurrentShipTosDataView(state),
    currentBillTo: getCurrentBillToState(state).value,
    countries: getCurrentCountries(state),
    shipToAddressFields: getAddressFieldsDataView(state).value?.shipToAddressFields,
});

const mapDispatchToProps = {
    updateShipTo,
    setCurrentShipTo,
    loadCurrentShipTos,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CurrentShippingAddressStyles {
    gridContainer?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyProps;
    editLinkGridItem?: GridItemProps;
    editLink?: LinkPresentationProps;
    addressFormModal?: ModalPresentationProps;
    addressInfoDisplayGridItem?: GridItemProps;
    useBillingAddressButtonGridItem?: GridItemProps;
    useBillingAddress?: UseBillingAddressStyles;
    // Starter Custom Code
    checkboxGridItem?: GridItemProps;
    centeringWrapper?: InjectableCss;
    useBillingAddressCheckbox?: CheckboxPresentationProps;
    wrapper?: InjectableCss;
    addressInfoDisplay?: AddressInfoDisplayStyles;
}

interface UseBillingAddressStyles {
    button?: ButtonPresentationProps;
}

export const currentShippingAddressStyles: CurrentShippingAddressStyles = {
    wrapper: addressProps.box,
    headingText: addressProps.title,
    addressInfoDisplay: {
        wrapper: addressProps.wrapper,
    },
    editLink: addressProps.linkEdit,
    addressFormModal: { sizeVariant: "medium" },
    useBillingAddressCheckbox: {
        css: css`
            ${x => x.theme.checkbox.defaultProps?.css}
            ${spacingStyles(null, pxToRem(10))}
        `,
    },
};

const styles = currentShippingAddressStyles;

const CurrentShippingAddress: React.FunctionComponent<Props> = (props: Props) => {
    const isB2BCustomer = useIsB2BCustomer(); // Store Custom Code

    const { currentShipTo, currentBillTo, currentShipTosDataView, loadCurrentShipTos } = props;

    useEffect(() => {
        // we reset all dataviews when adding a address, so we have to load these if they go away
        if (!currentShipTosDataView.value && !currentShipTosDataView.isLoading) {
            loadCurrentShipTos();
        }
    });

    const [modalIsOpen, setModalIsOpen] = React.useState(false);
    const trackShippingInfoChanged = ga.use.trackShippingInfoChanged(); // Starter Custom Code

    /* Starter Removed - return null */

    const billToAsShipTo = currentShipTosDataView?.value?.find(o => o.id === currentBillTo?.id); // Starter Custom Code - currentBillTo?

    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const editClickHandler = () => {
        setModalIsOpen(true);
    };
    const useBillingAddressHandler = () => {
        if (!billToAsShipTo) {
            return;
        }
        props.setCurrentShipTo({
            shipToId: billToAsShipTo.id,
        });
    };
    const formCancelHandler = (e: any) => {
        e.preventDefault();
        modalCloseHandler();
    };
    const formSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        if (!currentBillTo || !currentShipTo) {
            return;
        }

        props.updateShipTo({
            billToId: currentBillTo.id,
            shipTo: {
                ...currentShipTo,
                ...address,
            },
            expand: ["validation"],
        });
        trackShippingInfoChanged(); // Starter Custom Code
        modalCloseHandler();
    };

    const { countries, shipToAddressFields } = props;

    /* Starter Custom Code - Start */
    const isBillingAddress = currentShipTo?.id === currentBillTo?.id;

    return (
        <StyledWrapper {...styles.wrapper}>
            <Typography /* Starter Removed - as */ {...styles.headingText}>
                {translate("Shipping Information")}
            </Typography>
            {currentShipTosDataView.isLoading || !currentShipTo || !currentBillTo ? (
                <Loader />
            ) : (
                <>
                    <Checkbox
                        {...styles.useBillingAddressCheckbox}
                        checked={isBillingAddress}
                        onChange={useBillingAddressHandler}
                        disabled={isBillingAddress}
                    >
                        {translate("Use Billing Address")}
                    </Checkbox>
                    <AddressInfoDisplay
                        firstName={currentShipTo.firstName}
                        lastName={currentShipTo.lastName}
                        companyName={currentShipTo.companyName}
                        attention={currentShipTo.attention}
                        address1={currentShipTo.address1}
                        address2={currentShipTo.address2}
                        address3={currentShipTo.address3}
                        address4={currentShipTo.address4}
                        city={currentShipTo.city}
                        state={currentShipTo.state ? currentShipTo.state.abbreviation : undefined}
                        postalCode={currentShipTo.postalCode}
                        country={currentShipTo.country ? currentShipTo.country.abbreviation : undefined}
                        phone={currentShipTo.phone}
                        fax={currentShipTo.fax}
                        email={currentShipTo.email}
                        extendedStyles={styles.addressInfoDisplay}
                        data-test-selector="addressInfoDisplay_CurrentShippingAddress"
                    />
                    {!isBillingAddress && /* Store Custom Code */ !isB2BCustomer && (
                        <>
                            {countries && (
                                <Link
                                    {...styles.editLink}
                                    onClick={editClickHandler}
                                    data-test-selector="shipTo_Edit_Address"
                                >
                                    {translate("Edit")}
                                    <VisuallyHidden>{translate("Shipping Information")}</VisuallyHidden>
                                </Link>
                            )}
                            {countries && shipToAddressFields && (
                                <Modal
                                    headline={translate("Edit Shipping Information")}
                                    {...styles.addressFormModal}
                                    isOpen={modalIsOpen}
                                    handleClose={modalCloseHandler}
                                >
                                    <CustomerAddressForm
                                        address={currentShipTo}
                                        countries={countries}
                                        addressFieldDisplayCollection={shipToAddressFields}
                                        onCancel={formCancelHandler}
                                        onSubmit={formSubmitHandler}
                                    />
                                </Modal>
                            )}
                        </>
                    )}
                </>
            )}
        </StyledWrapper>
    );
    /* Starter Custom Code - End */
};

interface UseBillingAddressButtonProps {
    onClick: () => void;
}

const UseBillingAddressButton: React.FunctionComponent<UseBillingAddressButtonProps> = (
    props: UseBillingAddressButtonProps,
) => {
    const componentStyles: UseBillingAddressStyles = styles.useBillingAddress || {};

    return (
        <Button {...componentStyles.button} onClick={props.onClick}>
            {translate("Use Billing Address")}
        </Button>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CurrentShippingAddress),
    definition: {
        group: "Addresses",
        allowedContexts: ["AddressesPage"],
    },
};

export default widgetModule;
